import { MARKETING_COOKIE_KEYS } from '@/hooks/useMarketingCookies';
import { NextRequest, NextResponse } from 'next/server';
import { CookieHelper } from './cookies';

export interface UtmParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
}

const UTM_COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

export class UtmHelper {
  private static readonly UTM_PARAM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ] as const;
  private static readonly AD_PARAM_KEYS = [
    'gclid',
    'msclkid',
    'fbclid',
    'ours_device_id',
  ] as const;
  private static readonly TRACKING_PARAM_KEYS = [
    ...this.UTM_PARAM_KEYS,
    ...this.AD_PARAM_KEYS,
    ...MARKETING_COOKIE_KEYS,
  ] as const;

  /**
   * Extract UTM parameters from URL query string
   */
  static getUtmParams(): UtmParams {
    if (typeof window === 'undefined') {
      return this.getEmptyUtmParams();
    }

    const params = new URLSearchParams(window.location.search);
    const result: Partial<UtmParams> = {};

    this.UTM_PARAM_KEYS.forEach(key => {
      result[key] = params.get(key) || '';
    });

    return result as UtmParams;
  }

  /**
   * Extract UTM parameters from cookies (server-side safe)
   */
  static getUtmParamsFromCookies(cookieString?: string): UtmParams {
    const result: Partial<UtmParams> = {};

    if (cookieString || typeof document !== 'undefined') {
      const cookieHelper = new CookieHelper(
        cookieString || document.cookie || ''
      );

      this.UTM_PARAM_KEYS.forEach(key => {
        const cookie = cookieHelper.get(key);
        result[key] = cookie?.value || '';
      });
    } else {
      // Fallback to empty params
      this.UTM_PARAM_KEYS.forEach(key => {
        result[key] = '';
      });
    }

    return result as UtmParams;
  }

  /**
   * Get UTM parameters from URL first, then fallback to cookies
   * @param cookieString - Optional cookie string for server-side usage
   */
  static getUtmParamsWithFallback(cookieString?: string): UtmParams {
    // Try to get from URL first (client-side only)
    if (typeof window !== 'undefined') {
      const urlParams = this.getUtmParams();
      if (this.hasUtmParams(urlParams)) {
        return urlParams;
      }
    }

    // Fallback to cookies (works both client and server-side)
    return this.getUtmParamsFromCookies(cookieString);
  }

  /**
   * Check if any UTM parameters are present
   */
  static hasUtmParams(params: UtmParams): boolean {
    return this.UTM_PARAM_KEYS.some(key => params[key] !== '');
  }

  /**
   * Convert UTM params to a query string
   */
  static toQueryString(params: UtmParams): string {
    const queryParams = new URLSearchParams();

    this.UTM_PARAM_KEYS.forEach(key => {
      if (params[key]) {
        queryParams.set(key, params[key]);
      }
    });

    return queryParams.toString();
  }

  static appendToUrl(url: string, params: UtmParams): string {
    const target = new URL(url);

    this.UTM_PARAM_KEYS.forEach(key => {
      if (params[key]) {
        target.searchParams.set(key, params[key]);
      }
    });

    return target.toString();
  }

  // Store tracking params (UTMs) in cookies so we can send them in later
  // events / API calls.
  static handleTrackingParams(
    request: NextRequest,
    response: NextResponse
  ): void {
    const url = request.nextUrl;

    // Check if any tracking parameters are present in the URL
    const hasTrackingParams = this.TRACKING_PARAM_KEYS.some(key =>
      url.searchParams.has(key)
    );

    if (hasTrackingParams) {
      // Clear all existing tracking cookies first
      this.TRACKING_PARAM_KEYS.forEach(key => {
        response.cookies.delete(key);
      });

      // Set new cookies for each tracking parameter that exists
      this.TRACKING_PARAM_KEYS.forEach(key => {
        const value = url.searchParams.get(key);
        if (value) {
          response.cookies.set(key, value, {
            maxAge: UTM_COOKIE_MAX_AGE,
            httpOnly: false,
            secure: process.env.NODE_ENV === 'production',
            sameSite: 'lax',
            path: '/',
          });
        }
      });
    }
  }

  private static getEmptyUtmParams(): UtmParams {
    const result: Partial<UtmParams> = {};

    this.UTM_PARAM_KEYS.forEach(key => {
      result[key] = '';
    });

    return result as UtmParams;
  }
}
